import React from 'react'
import PropTypes from 'prop-types'
import { cloneDeep, every, isNil, isEmpty, compact, get, flatMap, includes, isUndefined } from 'lodash'
import Component from '../common/Component'
import UserContext from './BenefitsSetUserContext'
import FieldDefinition from './BenefitsSetFieldDefinition'
import HeaderRow from './BenefitsSetHeaderRow'
import BenefitRow from './BenefitsSetBenefitRow'
import TextField from '../common/TextField'
import { networkTierType } from './Helpers/tierTypeFactory'

export default class BenefitsSetBenefitsComponent extends Component {
  static propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    benefits: PropTypes.array.isRequired,
    benefitsSet: PropTypes.object.isRequired,
    reviews: PropTypes.array,
    rejections: PropTypes.array,
    handleChange: PropTypes.func.isRequired,
    handleBlur: PropTypes.func.isRequired,
    handleReviewChange: PropTypes.func.isRequired,
    handleHistory: PropTypes.func,
    handleTiersCountChange: PropTypes.func,
    userContext: PropTypes.instanceOf(UserContext).isRequired,
    errors: PropTypes.array,
    configuration: PropTypes.object.isRequired,
    displayValidFields: PropTypes.bool,
    displayType: PropTypes.string.isRequired,
    tiersCount: PropTypes.number.isRequired,
    benefitNames: PropTypes.array.isRequired,
    fieldGroups: PropTypes.array.isRequired,
    headerKey: PropTypes.string.isRequired,
    showHistory: PropTypes.bool,
    includeCoverageTier: PropTypes.bool,
    includeCoverageTierCount: PropTypes.bool,
    tiers_count_enabled: PropTypes.bool
  }

  static defaultProps = {
    tiers_count_enabled: true
  }

  static renderedProps = [
    'name',
    'benefits',
    'reviews',
    'rejections',
    'userContext',
    'errors',
    'displayValidFields',
    'configuration',
    'benefitsSet',
    'tiersCount'
  ]

  get additionalTiersCount() {
    return this.props.tiersCount - 2
  }

  get totalColumns() {
    return 2 + this.coverageTierCount + this.props.tiersCount
  }

  get coverageTierCount() {
    return this.props.includeCoverageTierCount ? 1 : 0
  }

  get indexedBenefits() {
    const benefits = this.props.benefits

    return this.props.benefitNames.map((name) => {
      const benefit = benefits.find((b) => b.name === name)

      return benefit || this.initialBenefitForName(name)
    })
  }

  get shouldDisplayBenefits() {
    if (this.props.userContext.isViewable) {
      return true
    }

    if (this.props.displayValidFields === true) {
      return true
    }

    const benefitsErrors = this.props.errors

    return !isNil(benefitsErrors) && !isEmpty(benefitsErrors)
  }

  get hideTiersField() {
    return !this.props.userContext.isEditable || !this.props.tiers_count_enabled
  }

  get tiersCountField() {
    if (this.hideTiersField) {
      return null
    }

    return (
      <div>
        <label htmlFor="tiers_count">Tiers</label>
        <TextField
          id="tiers_count"
          name="tiers_count"
          type="number"
          min={2}
          max={10}
          step={1}
          handleChange={this.handleTiersCountChange}
          value={this.props.tiersCount}
          inputCssClass=""
        />
      </div>
    )
  }

  get benefitsHeaderForNetworkTier() {
    return (
      <tr className="header-row" key={`${this.props.headerKey}`}>
        <td key="header-1" colSpan={1}>{this.props.label}</td>
        <td key="header-2" colSpan={this.totalColumns - 1 || 1}>
          {this.tiersCountField}
        </td>
      </tr>
    )
  }

  get rows() {
    const benefitGroups = this.props.fieldGroups
    const reviews = this.props.reviews || []
    const showHistory = this.props.showHistory
    const keyBenefits = this.keyBenefits()

    const rows = []

    let accumulator = 0

    if (!this.shouldDisplayBenefits) {
      return []
    }

    benefitGroups.forEach((group, groupIndex) => {
      if (!isUndefined(group.tier)) {
        const displayGroup = group.fields.map((f) => ({
          name: f.name,
          label: f.label,
          display: this.shouldDisplayBenefitRow(group.name, f.name)
        }))

        if (every(displayGroup, { display: false })) {
          accumulator += (group.fields.length)
          return
        }
        rows.push(this.benefitHeaderRow(group.name, group.tier))

        displayGroup.forEach(({ name, label, display }, index) => {
          if (!display) {
            return
          }

          const required = includes(keyBenefits, name)

          rows.push(
            this.benefitRow(
              name,
              (index + accumulator).toString(),
              label,
              reviews[index + accumulator],
              this.handleChange,
              this.handleReviewChange,
              this.handleHistory,
              showHistory,
              required,
              group.tier
            )
          )
        })

        accumulator += (group.fields.length)
      } else {
        if (groupIndex === 0) {
          rows.push(this.benefitsHeaderForNetworkTier)
        }

        const displayGroup = group.fields.map((f) => ({
          name: f.name,
          label: f.label,
          display: this.shouldDisplayBenefitRow(group.name, f.name)
        }))

        if (every(displayGroup, { display: false })) {
          accumulator += (group.fields.length)
          return
        }

        rows.push(this.benefitHeaderRow(group.name, networkTierType))

        displayGroup.forEach(({ name, label, display }, index) => {
          if (!display) {
            return
          }

          const required = includes(keyBenefits, name)

          rows.push(
            this.benefitRow(
              name,
              (index + accumulator).toString(),
              label,
              reviews[index + accumulator],
              this.handleChange,
              this.handleReviewChange,
              this.handleHistory,
              showHistory,
              required,
              networkTierType
            )
          )
        })

        accumulator += (group.fields.length)
      }
    })

    return rows
  }

  initialBenefitForName = (name) => {
    const tierType = this.tierTypeFromName(name)
    return tierType.initialBenefitForName(name)
  }

  benefitValueForName = (name) => this.props.benefits.find((b) => b.name === name)

  errorForName = (name) => {
    const index = this.props.benefitNames.indexOf(name)
    return get(this.props.errors, index)
  }

  handleChange = (index, value) => {
    const benefits = this.indexedBenefits

    if (isNil(value)) {
      benefits[index] = this.initialBenefitForName(this.props.benefitNames[index])
    } else {
      benefits[index] = value
    }

    if (isEmpty(compact(benefits))) {
      this.props.handleChange(this.props.name, null)
      return
    }

    this.props.handleChange(this.props.name, benefits)
  }

  handleReviewChange = (index, value) => {
    const reviews = cloneDeep(this.props.reviews) || []

    if (isNil(value)) {
      reviews[index] = null
    } else {
      reviews[index] = value
    }

    if (isEmpty(compact(reviews))) {
      this.props.handleReviewChange('benefit_reviews', null)
      return
    }

    this.props.handleReviewChange('benefit_reviews', reviews)
  }

  handleTiersCountChange = (name, value) => {
    this.props.handleTiersCountChange(parseInt(value, 10))
  }

  handleHistory = (name, benefitName) => {
    this.props.handleHistory(name, benefitName)
  }

  benefitRow = (name, index, label, review, handleChange, handleReviewChange,
    handleHistory, showHistory, required, tier) => {
    const definition = this.benefitFieldDefinition(name, label)

    const benefitName = get(definition.value, 'name')

    let rejections = []

    if (!isNil(benefitName)) {
      rejections = this.rejectionsForBenefit(benefitName)
    }

    return (
      <BenefitRow
        key={name}
        label={label}
        name={index}
        review={review}
        rejections={rejections}
        required={required}
        handleBlur={this.props.handleBlur}
        handleChange={handleChange}
        handleReviewChange={handleReviewChange}
        handleHistory={handleHistory}
        showHistory={showHistory}
        additionalTiersCount={this.additionalTiersCount}
        userContext={this.props.userContext}
        fieldDefinition={definition}
        includeCoverageTier={this.props.includeCoverageTier}
        includeCoverageTierCount={this.props.includeCoverageTierCount}
        benefitsSet={this.props.benefitsSet}
        tierType={tier}
      />
    )
  }

  rejectionsForBenefit = (benefitName) => {
    const rejections = this.props.rejections || []

    return compact(rejections.map((r) => {
      const review = r.review.find((benefitReview) => (
        benefitReview.benefit_name === benefitName
      ))

      if (isNil(review)) {
        return null
      }

      return {
        review_id: r.review_id,
        created_at: r.created_at,
        review
      }
    }))
  }

  shouldDisplayBenefitRow = (groupName, name) => {
    if (this.props.includeCoverageTierCount && groupName !== this.props.displayType) {
      return false
    }

    if (isNil(this.props.displayValidFields) || this.props.displayValidFields === true) {
      return true
    }

    return !isNil(this.errorForName(name))
  }

  keyBenefits = () => {
    const { benefitsSet, configuration } = this.props
    const keyBenefitsConfigs = configuration.key_benefits_configs

    return compact(flatMap(keyBenefitsConfigs, (config) => {
      const where = config.where
      if (where == null || get(benefitsSet.metadatum, where.field) === where.value) {
        return config.benefits
      }
      return null
    }))
  }

  tierTypeFromName = (name) => {
    if (isUndefined(this.props.fieldGroups)) { return networkTierType }
    const index = this.props.fieldGroups.findIndex((group) => group.fields.find((b) => b.name === name))

    if (index >= 0 && !isUndefined(this.props.fieldGroups[index].tier)) {
      return this.props.fieldGroups[index].tier
    }

    return networkTierType
  }

  tierHeaderLabels = (tierType) => {
    return tierType.headers(this.additionalTiersCount, this.props.includeCoverageTier)
  }

  benefitFieldDefinition(name, label) {
    return new FieldDefinition(
      name,
      { label },
      this.benefitValueForName(name) || { name, value: name },
      this.errorForName(name),
      this.props.benefitsSet,
      this.props.configuration,
      this.props.userContext
    )
  }

  headerRow(labels) {
    return (
      <HeaderRow
        key={`header-${labels[0]}`}
        labels={labels}
        totalColumns={this.totalColumns}
        headerKey={this.props.headerKey}
      />
    )
  }

  benefitHeaderRow(label, tierType) {
    return this.headerRow([label].concat(this.tierHeaderLabels(tierType)))
  }

  render() {
    return (
      <tbody id="benefit-rows">
        {this.rows}
      </tbody>
    )
  }
}

